import { Button } from "@circleco/compass/components/Button";
import {
  DateTimePicker,
  type DateTimePickerProps,
} from "@circleco/compass/components/DateTimePicker";
import {
  Popover,
  type PopoverProps,
} from "@circleco/compass/components/Popover";
import { addYears, endOfYear } from "date-fns";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";

export interface ScheduleDateTimePickerPopoverProps {
  align?: PopoverProps["align"];
  anchor?: PopoverProps["anchor"];
  disabledDays?: DateTimePickerProps["disabledDays"];
  endMonth?: DateTimePickerProps["endMonth"];
  onOpenChange: (open: boolean) => void;
  onSave: (date: Date) => void;
  open: boolean;
  side?: PopoverProps["side"];
  trigger?: PopoverProps["trigger"];
  value?: Date;
}

export const ScheduleDateTimePickerPopover = ({
  align = "end",
  anchor,
  disabledDays,
  endMonth = endOfYear(addYears(new Date(), 10)),
  onOpenChange,
  onSave,
  open,
  side = "bottom",
  trigger,
  value,
}: ScheduleDateTimePickerPopoverProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);

  useEffect(() => {
    if (open) {
      setSelectedDate(value);
    }
  }, [open, value]);

  return (
    <Popover
      align={align}
      anchor={anchor}
      className="w-[18rem] p-0"
      closeOnContentClick={false}
      onOpenChange={onOpenChange}
      open={open}
      side={side}
      sideOffset={8}
      trigger={trigger}
    >
      <DateTimePicker
        disabledDays={disabledDays}
        endMonth={endMonth}
        onChange={setSelectedDate}
        value={selectedDate}
        timeFormat="12h"
      />
      <div className="border-primary grid grid-cols-2 gap-3 border-t p-3">
        <Button
          fullWidth
          onClick={() => onOpenChange(false)}
          type="button"
          variant="outline"
        >
          {t("cancel")}
        </Button>
        <Button
          disabled={!selectedDate}
          fullWidth
          onClick={() => {
            if (!selectedDate) return;

            onSave(selectedDate);
            onOpenChange(false);
          }}
          type="button"
        >
          {t("save")}
        </Button>
      </div>
    </Popover>
  );
};
